// 「いま見えている位置」にオーバーレイを重ねるモーダル（テンプレ差し替え）
// フックは js-* のみ参照
export function initModal(opts = {}) {
  const sel = {
    modal: opts.modal ?? ".js-modal",
    trigger: opts.trigger ?? ".js-modal-open",
    panelSel: opts.panelSel ?? ".js-modal-panel",
    bodySel: opts.bodySel ?? ".js-modal-body",
    closeSel: opts.closeSel ?? ".js-modal-close",
    backdrop: opts.backdrop ?? ".js-modal-backdrop",
    tplSel: opts.tplSel ?? ".js-modal-tpl",
    scopeSel: opts.scopeSel ?? "[data-modal-scope], .js-modal-scope",
  };

  // 明示 root があればそれを優先。なければ後述の auto 検出。
  let configuredRoot = opts.root ?? null;

  const modalEl = typeof sel.modal === "string" ? document.querySelector(sel.modal) : sel.modal;
  if (!modalEl) return;

  const panelEl = modalEl.querySelector(sel.panelSel);
  const bodyEl = modalEl.querySelector(sel.bodySel);
  if (!panelEl || !bodyEl) return;

  // ---- helpers -------------------------------------------------------------
  const isDoc = (el) => el === document.documentElement || el === document.body;

  // ボタンから辿って「スクロールしている祖先」を探す
  const findScrollContainer = (startEl) => {
    if (configuredRoot) return configuredRoot;

    let el = startEl?.parentElement;
    while (el && el !== document.body) {
      const cs = getComputedStyle(el);
      const oy = cs.overflowY;
      const scrollable = oy === "auto" || oy === "scroll" || oy === "overlay";
      if (scrollable && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    // 祖先に無ければ、文書スクロール
    return document.scrollingElement || document.documentElement;
  };

  const getTop = (root) => (isDoc(root) ? window.scrollY : root.scrollTop);
  const getClientH = (root) => (isDoc(root) ? window.innerHeight : root.clientHeight);

  // 近傍 or data-tpl 指定で <template> を取得
  const findTemplate = (btn) => {
    const direct = btn.getAttribute("data-tpl");
    if (direct) {
      const el = document.querySelector(direct);
      if (el && el.tagName.toLowerCase() === "template") return el;
    }
    const scope = btn.closest(sel.scopeSel) || btn.parentElement;
    return scope?.querySelector(sel.tplSel) || (btn.nextElementSibling?.matches?.(sel.tplSel) && btn.nextElementSibling);
  };

  // 背景スクロール抑止（iOS含む）
  let preventBg; // 後で作る（rootに依存しない）
  const makePreventBg = () => (e) => {
    if (!panelEl.contains(e.target)) e.preventDefault();
  };

  // ---- state ---------------------------------------------------------------
  let lastActive = null;
  let currentRoot = configuredRoot || document.documentElement;
  let onResize = null;

  // 「今見えている位置」に .c-modal を配置
  const positionOverlayToRootViewport = () => {
    const topNow = getTop(currentRoot);
    const hNow = getClientH(currentRoot);

    // top/bottom 同時指定は避けるため、CSSで bottom は指定しないこと
    modalEl.style.top = topNow + "px";
    modalEl.style.height = hNow + "px";
  };

  // ---- open / close --------------------------------------------------------
  const open = (btn) => {
    // その時点でのスクロールコンテナを決定
    currentRoot = configuredRoot || findScrollContainer(btn);

    // 差し替え
    const tpl = findTemplate(btn);
    bodyEl.replaceChildren();
    if (tpl && "content" in tpl) bodyEl.appendChild(tpl.content.cloneNode(true));

    // 任意：背景色ヒント（data-modal-bg / --bg）
    const host = btn.closest(sel.scopeSel) || btn.closest("[data-modal-bg]") || btn;
    const bg = host?.dataset?.modalBg || host?.style?.getPropertyValue?.("--bg");
    if (bg) modalEl.style.setProperty("--bg", bg.trim());

    // 位置を「今見えている範囲」に合わせる
    positionOverlayToRootViewport();

    // パネルは毎回先頭から
    panelEl.scrollTop = 0;
    panelEl.scrollLeft = 0;

    // 表示＆ロック
    modalEl.hidden = false;
    lastActive = document.activeElement;
    currentRoot.classList.add("is-modal-open");
    document.body.classList.add("is-modal-open");

    // 背景スクロール抑止
    preventBg = makePreventBg();
    modalEl.addEventListener("wheel", preventBg, { passive: false });
    modalEl.addEventListener("touchmove", preventBg, { passive: false });

    // 回転/リサイズでも追従
    onResize = () => {
      if (!modalEl.hidden) positionOverlayToRootViewport();
    };
    window.addEventListener("resize", onResize, { passive: true });

    // フォーカス移動（スクロールさせない）
    requestAnimationFrame(() => {
      positionOverlayToRootViewport(); // 念のためもう一度
      panelEl.scrollTop = 0;
      try {
        panelEl.focus({ preventScroll: true });
      } catch {
        panelEl.focus();
      }
    });
  };

  const close = () => {
    modalEl.hidden = true;

    currentRoot.classList.remove("is-modal-open");
    document.body.classList.remove("is-modal-open");

    modalEl.removeEventListener("wheel", preventBg, { passive: false });
    modalEl.removeEventListener("touchmove", preventBg, { passive: false });
    window.removeEventListener("resize", onResize);

    // 位置記憶しない
    modalEl.style.top = "";
    modalEl.style.height = "";
    panelEl.scrollTop = 0;

    if (lastActive && document.contains(lastActive)) {
      try {
        lastActive.focus({ preventScroll: true });
      } catch {
        lastActive.focus();
      }
    }
  };

  // ---- events --------------------------------------------------------------
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(sel.trigger);
    if (btn) open(btn);
  });

  modalEl.addEventListener("click", (e) => {
    if (e.target.matches(sel.backdrop) || e.target.closest(sel.closeSel)) close();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !modalEl.hidden) close();
  });

  return { open, close, el: modalEl };
}
